export type StateStatus = 'legal' | 'gray-area' | 'illegal'

export interface City {
  name: string
  slug: string
}

export interface State {
  name: string
  slug: string
  status: StateStatus
  summary: string
  keyFacts: string[]
  cities: City[]
}

export const states: State[] = [
  {
    name: 'Alabama',
    slug: 'alabama',
    status: 'gray-area',
    summary:
      'Alabama has some of the strictest gambling laws in the South, with no commercial casinos and no licensed online poker operators. The state has no lottery and has historically resisted gambling expansion, though offshore sites remain accessible to players.',
    keyFacts: [
      'No state-licensed online poker sites operate in Alabama',
      'Commercial casinos and a state lottery are both prohibited',
      'Tribal gaming is limited to electronic bingo at a handful of locations',
      'Offshore poker sites serve Alabama players but operate without state oversight',
      'Repeated legislative efforts to expand gambling have failed',
    ],
    cities: [
      { name: 'Birmingham', slug: 'birmingham' },
      { name: 'Montgomery', slug: 'montgomery' },
      { name: 'Huntsville', slug: 'huntsville' },
      { name: 'Mobile', slug: 'mobile' },
      { name: 'Tuscaloosa', slug: 'tuscaloosa' },
    ],
  },
  {
    name: 'Alaska',
    slug: 'alaska',
    status: 'gray-area',
    summary:
      'Alaska permits very little commercial gambling — no casinos, no lottery, and no licensed online poker. The state allows charitable gaming and some pull-tab operations, but real-money online poker exists only through offshore platforms with no state regulation.',
    keyFacts: [
      'No state lottery or commercial casinos exist in Alaska',
      'Charitable gaming and pull-tabs are the primary legal gambling forms',
      'No licensed online poker operators serve the state',
      'Offshore sites are accessible but unregulated by the state',
      'Remote geography has limited the political push for expanded gambling',
    ],
    cities: [
      { name: 'Anchorage', slug: 'anchorage' },
      { name: 'Fairbanks', slug: 'fairbanks' },
      { name: 'Juneau', slug: 'juneau' },
      { name: 'Sitka', slug: 'sitka' },
      { name: 'Ketchikan', slug: 'ketchikan' },
    ],
  },
  {
    name: 'Arizona',
    slug: 'arizona',
    status: 'gray-area',
    summary:
      'Arizona has a robust tribal gaming industry and legalized sports betting in 2021, but has not moved to regulate online poker. Tribal compacts govern most gambling activity, and no state framework exists for licensed online poker rooms.',
    keyFacts: [
      'Sports betting launched in September 2021 with over 20 licensed operators',
      'Tribal gaming compacts govern casino gambling across the state',
      'No licensed online poker sites operate within Arizona',
      'Offshore platforms are accessible but operate outside state law',
      'Legislative interest in iGaming has grown following sports betting success',
    ],
    cities: [
      { name: 'Phoenix', slug: 'phoenix' },
      { name: 'Tucson', slug: 'tucson' },
      { name: 'Mesa', slug: 'mesa' },
      { name: 'Chandler', slug: 'chandler' },
      { name: 'Scottsdale', slug: 'scottsdale' },
    ],
  },
  {
    name: 'Arkansas',
    slug: 'arkansas',
    status: 'gray-area',
    summary:
      'Arkansas expanded gambling significantly with casino approvals in 2018 and launched sports betting in 2022, but online poker remains unregulated. The state is gradually opening to new gambling formats though no online poker legislation has passed.',
    keyFacts: [
      'Four commercial casinos operate under a 2018 voter amendment',
      'Sports betting launched in 2022 through licensed casinos',
      'No framework exists for licensed online poker',
      'Offshore poker sites are accessible to residents',
      'Gambling expansion continues but online poker has not been prioritized',
    ],
    cities: [
      { name: 'Little Rock', slug: 'little-rock' },
      { name: 'Fort Smith', slug: 'fort-smith' },
      { name: 'Fayetteville', slug: 'fayetteville' },
      { name: 'Springdale', slug: 'springdale' },
      { name: 'Jonesboro', slug: 'jonesboro' },
    ],
  },
  {
    name: 'California',
    slug: 'california',
    status: 'gray-area',
    summary:
      'California is the largest potential regulated online poker market in the United States, but years of legislative attempts have collapsed under competing tribal and card room interests. Despite a massive poker culture — home to Commerce Casino and the WSOP — residents rely on offshore platforms for online play.',
    keyFacts: [
      'California has the most active live poker scene in the US with hundreds of card rooms',
      'Multiple online poker bills have died in the state legislature since 2010',
      'Tribal casinos and card rooms have clashed over player pool sharing and bad actor provisions',
      'No licensed online poker operators serve California residents',
      'If regulated, California would likely be the largest online poker market in the country',
    ],
    cities: [
      { name: 'Los Angeles', slug: 'los-angeles' },
      { name: 'San Francisco', slug: 'san-francisco' },
      { name: 'San Diego', slug: 'san-diego' },
      { name: 'San Jose', slug: 'san-jose' },
      { name: 'Sacramento', slug: 'sacramento' },
    ],
  },
  {
    name: 'Colorado',
    slug: 'colorado',
    status: 'gray-area',
    summary:
      'Colorado legalized sports betting in 2020 and has land-based casinos in Black Hawk, Central City, and Cripple Creek, but no online poker regulation exists. The sports betting market has been competitive, though iGaming legislation has not advanced.',
    keyFacts: [
      'Sports betting launched in May 2020 with a competitive multi-operator market',
      'Land-based casinos operate in three historic mining towns',
      'No state-licensed online poker platforms exist',
      'Offshore poker sites are accessible to residents',
      'iGaming bills have been introduced but have not passed',
    ],
    cities: [
      { name: 'Denver', slug: 'denver' },
      { name: 'Colorado Springs', slug: 'colorado-springs' },
      { name: 'Aurora', slug: 'aurora' },
      { name: 'Fort Collins', slug: 'fort-collins' },
      { name: 'Boulder', slug: 'boulder' },
    ],
  },
  {
    name: 'Connecticut',
    slug: 'connecticut',
    status: 'legal',
    summary:
      'Connecticut launched regulated online poker in October 2021, becoming one of a small group of states with a fully legal market. The state authorized two operators — WSOP and DraftKings Poker — through its tribal gaming partners Mohegan Sun and Foxwoods.',
    keyFacts: [
      'Online poker launched in October 2021 alongside online casino gaming',
      'WSOP (via Mohegan Sun) and DraftKings Poker (via Foxwoods) are licensed operators',
      'Players must be physically located in Connecticut to play for real money',
      'Geolocation verification is required at login',
      'Connecticut has a compact with the Mashantucket Pequot and Mohegan tribes governing the market',
    ],
    cities: [
      { name: 'Bridgeport', slug: 'bridgeport' },
      { name: 'New Haven', slug: 'new-haven' },
      { name: 'Hartford', slug: 'hartford' },
      { name: 'Stamford', slug: 'stamford' },
      { name: 'Waterbury', slug: 'waterbury' },
    ],
  },
  {
    name: 'Delaware',
    slug: 'delaware',
    status: 'legal',
    summary:
      'Delaware was one of three states to launch regulated online poker in 2013, making it a pioneer in the US market. The state operates through the Delaware Lottery using 888poker technology, and participates in a multi-state player pool with Nevada and New Jersey.',
    keyFacts: [
      'Online poker launched in November 2013 — among the first in the nation',
      'Operated through the Delaware Lottery using 888poker software',
      'Shares a player pool with Nevada and New Jersey under a multi-state compact',
      'Three Delaware racinos (Dover Downs, Harrington, Delaware Park) serve as the online brands',
      'The small population means multi-state liquidity is essential for healthy games',
    ],
    cities: [
      { name: 'Wilmington', slug: 'wilmington' },
      { name: 'Dover', slug: 'dover' },
      { name: 'Newark', slug: 'newark' },
      { name: 'Middletown', slug: 'middletown' },
      { name: 'Smyrna', slug: 'smyrna' },
    ],
  },
  {
    name: 'Florida',
    slug: 'florida',
    status: 'gray-area',
    summary:
      'Florida has a major live poker industry anchored by the Seminole Hard Rock and numerous card rooms, but online poker remains unregulated. A 2021 gaming compact gave the Seminole Tribe broad authority over sports betting, but online poker has not been part of any licensing framework.',
    keyFacts: [
      'Florida hosts some of the busiest poker rooms in the world, including Seminole Hard Rock Hollywood',
      'No state-licensed online poker operators serve Florida',
      'The 2021 Seminole Tribe compact covers sports betting but not online poker',
      'Offshore platforms are widely used by Florida residents',
      "Florida's large and active poker community makes it a key target for future regulation",
    ],
    cities: [
      { name: 'Jacksonville', slug: 'jacksonville' },
      { name: 'Miami', slug: 'miami' },
      { name: 'Tampa', slug: 'tampa' },
      { name: 'Orlando', slug: 'orlando' },
      { name: 'St. Petersburg', slug: 'st-petersburg' },
    ],
  },
  {
    name: 'Georgia',
    slug: 'georgia',
    status: 'gray-area',
    summary:
      'Georgia has no commercial gambling industry — no casinos, no lottery-linked poker, and no licensed online gaming. The state does have a lottery for education funding, but poker and casino-style games are not regulated in any form.',
    keyFacts: [
      'No commercial casinos or card rooms operate in Georgia',
      'The state lottery funds education programs but does not include poker',
      'Tribal gaming does not operate in Georgia',
      'No online poker legislation has been seriously advanced',
      'Offshore sites serve Georgia residents without state licensing',
    ],
    cities: [
      { name: 'Atlanta', slug: 'atlanta' },
      { name: 'Augusta', slug: 'augusta' },
      { name: 'Columbus', slug: 'columbus' },
      { name: 'Macon', slug: 'macon' },
      { name: 'Savannah', slug: 'savannah' },
    ],
  },
  {
    name: 'Hawaii',
    slug: 'hawaii',
    status: 'illegal',
    summary:
      'Hawaii has some of the most restrictive gambling laws in the country. Virtually all forms of gambling are prohibited by state law, with no lottery, no casinos, and no licensed poker in any form. Online poker is not licensed and playing on offshore sites exists in a legally hostile environment.',
    keyFacts: [
      'Hawaii is one of only two states with no legal form of commercial gambling',
      'No lottery, no casinos, no licensed card rooms exist in the state',
      'State law broadly prohibits gambling with limited exceptions for social play',
      'No online poker operators are licensed to serve Hawaii',
      'Repeated gambling expansion proposals have failed to gain traction',
    ],
    cities: [
      { name: 'Honolulu', slug: 'honolulu' },
      { name: 'Pearl City', slug: 'pearl-city' },
      { name: 'Hilo', slug: 'hilo' },
      { name: 'Kailua', slug: 'kailua' },
      { name: 'Waipahu', slug: 'waipahu' },
    ],
  },
  {
    name: 'Idaho',
    slug: 'idaho',
    status: 'gray-area',
    summary:
      'Idaho permits tribal gaming under IGRA but has strict laws against commercial gambling. No licensed online poker exists, and the state has not pursued iGaming legislation. Residents who play on offshore sites do so without state regulatory protection.',
    keyFacts: [
      'Tribal gaming compacts allow slot machines and some table games on tribal land',
      'Commercial casinos and card rooms are prohibited',
      'No state lottery exists in Idaho',
      'No licensed online poker platforms operate in Idaho',
      'State gambling laws are among the more restrictive in the Northwest',
    ],
    cities: [
      { name: 'Boise', slug: 'boise' },
      { name: 'Meridian', slug: 'meridian' },
      { name: 'Nampa', slug: 'nampa' },
      { name: 'Idaho Falls', slug: 'idaho-falls' },
      { name: 'Pocatello', slug: 'pocatello' },
    ],
  },
  {
    name: 'Illinois',
    slug: 'illinois',
    status: 'gray-area',
    summary:
      'Illinois has a large commercial casino industry and legalized sports betting in 2019, but has not moved to regulate online poker. iGaming legislation has been discussed but has not advanced, leaving residents to use offshore platforms for online poker.',
    keyFacts: [
      'Sports betting launched in March 2020 with a robust multi-operator market',
      'Ten commercial casinos operate in the state including Rivers Casino Des Plaines',
      'No licensed online poker sites exist in Illinois',
      'iGaming bills have been introduced but stalled in the legislature',
      'Illinois is considered a high-value target for online poker due to its large population',
    ],
    cities: [
      { name: 'Chicago', slug: 'chicago' },
      { name: 'Aurora', slug: 'aurora' },
      { name: 'Naperville', slug: 'naperville' },
      { name: 'Joliet', slug: 'joliet' },
      { name: 'Rockford', slug: 'rockford' },
    ],
  },
  {
    name: 'Indiana',
    slug: 'indiana',
    status: 'gray-area',
    summary:
      'Indiana has a well-established land-based casino industry and launched mobile sports betting in 2019, but online poker legislation has not advanced. Residents play on offshore sites without state licensing or consumer protections.',
    keyFacts: [
      'Sports betting launched in October 2019 with multiple licensed operators',
      'Thirteen licensed casinos operate across the state',
      'No online poker regulation exists despite an active gambling market',
      'Offshore platforms are the primary option for online poker players',
      'iGaming has been discussed but has not passed the General Assembly',
    ],
    cities: [
      { name: 'Indianapolis', slug: 'indianapolis' },
      { name: 'Fort Wayne', slug: 'fort-wayne' },
      { name: 'Evansville', slug: 'evansville' },
      { name: 'South Bend', slug: 'south-bend' },
      { name: 'Carmel', slug: 'carmel' },
    ],
  },
  {
    name: 'Iowa',
    slug: 'iowa',
    status: 'gray-area',
    summary:
      'Iowa was a pioneer in land-based gambling and launched sports betting in 2019, but online poker has not been regulated. The state has numerous riverboat and land-based casinos, giving it one of the most mature gambling markets in the Midwest.',
    keyFacts: [
      'Sports betting launched in August 2019, making Iowa an early adopter',
      'Over 20 licensed casinos operate throughout the state',
      'No licensed online poker operators serve Iowa',
      'Offshore sites are available to residents without state-level protections',
      "Iowa's gambling-friendly culture may eventually lead to iGaming regulation",
    ],
    cities: [
      { name: 'Des Moines', slug: 'des-moines' },
      { name: 'Cedar Rapids', slug: 'cedar-rapids' },
      { name: 'Davenport', slug: 'davenport' },
      { name: 'Sioux City', slug: 'sioux-city' },
      { name: 'Iowa City', slug: 'iowa-city' },
    ],
  },
  {
    name: 'Kansas',
    slug: 'kansas',
    status: 'gray-area',
    summary:
      'Kansas has a small commercial casino sector and launched sports betting in 2022, but no online poker regulation exists. The state has historically been conservative on gambling expansion, and iGaming has not been part of legislative discussions.',
    keyFacts: [
      'Sports betting launched in September 2022 through state-owned casinos',
      'Four state-owned casinos operate in the state',
      'No online poker framework has been introduced',
      'Offshore sites serve Kansas residents without state oversight',
      'Tribal gaming also operates under federal compacts in the state',
    ],
    cities: [
      { name: 'Wichita', slug: 'wichita' },
      { name: 'Overland Park', slug: 'overland-park' },
      { name: 'Kansas City', slug: 'kansas-city' },
      { name: 'Olathe', slug: 'olathe' },
      { name: 'Topeka', slug: 'topeka' },
    ],
  },
  {
    name: 'Kentucky',
    slug: 'kentucky',
    status: 'gray-area',
    summary:
      'Kentucky is famous for horse racing and has a long gambling history, but casino gambling and online poker are not regulated. The state legalized historical horse racing machines and sports betting in 2023, marking a shift in gambling policy.',
    keyFacts: [
      'Sports betting launched in September 2023 through licensed sportsbooks',
      "Horse racing is deeply embedded in the state's culture and economy",
      'No licensed online poker sites operate in Kentucky',
      'Historical horse racing machines are legal at tracks and off-track betting facilities',
      'Online poker has not been part of the recent gambling expansion discussions',
    ],
    cities: [
      { name: 'Louisville', slug: 'louisville' },
      { name: 'Lexington', slug: 'lexington' },
      { name: 'Bowling Green', slug: 'bowling-green' },
      { name: 'Owensboro', slug: 'owensboro' },
      { name: 'Covington', slug: 'covington' },
    ],
  },
  {
    name: 'Louisiana',
    slug: 'louisiana',
    status: 'gray-area',
    summary:
      "Louisiana has a mature land-based gambling industry with riverboat casinos and a land-based casino in New Orleans, plus sports betting that launched in 2022. Online poker has not been regulated despite the state's gambling-friendly culture.",
    keyFacts: [
      'Sports betting launched in January 2022 across most parishes',
      "One land-based casino (Harrah's New Orleans) and multiple riverboats operate",
      'Video poker in bars and restaurants is uniquely popular in Louisiana',
      'No licensed online poker operators serve the state',
      'Offshore sites are widely used by residents',
    ],
    cities: [
      { name: 'New Orleans', slug: 'new-orleans' },
      { name: 'Baton Rouge', slug: 'baton-rouge' },
      { name: 'Shreveport', slug: 'shreveport' },
      { name: 'Metairie', slug: 'metairie' },
      { name: 'Lafayette', slug: 'lafayette' },
    ],
  },
  {
    name: 'Maine',
    slug: 'maine',
    status: 'gray-area',
    summary:
      'Maine has limited commercial gambling but legalized sports betting in 2022 and tribal gaming exists under IGRA. Online poker has not been licensed, and residents looking to play online use offshore platforms.',
    keyFacts: [
      'Sports betting legislation was signed in 2022',
      'Hollywood Casino Hotel & Raceway is the primary commercial gaming facility',
      'Tribal gaming compacts cover gaming on tribal lands',
      'No online poker regulation exists in Maine',
      'The small population limits the economic case for a standalone online poker market',
    ],
    cities: [
      { name: 'Portland', slug: 'portland' },
      { name: 'Lewiston', slug: 'lewiston' },
      { name: 'Bangor', slug: 'bangor' },
      { name: 'South Portland', slug: 'south-portland' },
      { name: 'Auburn', slug: 'auburn' },
    ],
  },
  {
    name: 'Maryland',
    slug: 'maryland',
    status: 'gray-area',
    summary:
      'Maryland has six commercial casinos and launched sports betting in late 2021, but iGaming including online poker remains off the table legislatively. Despite a thriving land-based market near the DC metro area, online poker regulation has not moved forward.',
    keyFacts: [
      'Sports betting launched in December 2021 with retail and mobile options',
      'Six commercial casinos operate including MGM National Harbor and Live! Casino',
      'No licensed online poker sites serve Maryland residents',
      'iGaming legislation has been proposed but has not passed',
      "Maryland's proximity to DC and large population make it an attractive future market",
    ],
    cities: [
      { name: 'Baltimore', slug: 'baltimore' },
      { name: 'Frederick', slug: 'frederick' },
      { name: 'Rockville', slug: 'rockville' },
      { name: 'Gaithersburg', slug: 'gaithersburg' },
      { name: 'Bowie', slug: 'bowie' },
    ],
  },
  {
    name: 'Massachusetts',
    slug: 'massachusetts',
    status: 'gray-area',
    summary:
      "Massachusetts launched sports betting in early 2023 and operates three commercial casinos, but online poker has not been part of the state's gambling expansion. No iGaming legislation has advanced despite a competitive sports betting market.",
    keyFacts: [
      'Sports betting launched in January 2023 (retail) and March 2023 (mobile)',
      'Three commercial casinos operate: MGM Springfield, Encore Boston Harbor, and Plainridge Park',
      'No licensed online poker platforms serve Massachusetts',
      'iGaming has not been part of the state gambling agenda',
      'The large, affluent population makes Massachusetts a prime candidate for future regulation',
    ],
    cities: [
      { name: 'Boston', slug: 'boston' },
      { name: 'Worcester', slug: 'worcester' },
      { name: 'Springfield', slug: 'springfield' },
      { name: 'Lowell', slug: 'lowell' },
      { name: 'Cambridge', slug: 'cambridge' },
    ],
  },
  {
    name: 'Michigan',
    slug: 'michigan',
    status: 'legal',
    summary:
      'Michigan launched regulated online poker in January 2021 as part of a broad iGaming bill, creating one of the most competitive markets in the country. Multiple operators — including PokerStars, BetMGM, and WSOP — are licensed and share player pools with other regulated states.',
    keyFacts: [
      'Online poker launched in January 2021 alongside online casino games',
      'Licensed operators include PokerStars MI, BetMGM Poker, WSOP, and BetRivers',
      'Michigan participates in multi-state player pool agreements',
      'Players must be physically located in Michigan to play for real money',
      'The Lawful Internet Gaming Act of 2019 created the licensing framework',
    ],
    cities: [
      { name: 'Detroit', slug: 'detroit' },
      { name: 'Grand Rapids', slug: 'grand-rapids' },
      { name: 'Warren', slug: 'warren' },
      { name: 'Sterling Heights', slug: 'sterling-heights' },
      { name: 'Ann Arbor', slug: 'ann-arbor' },
    ],
  },
  {
    name: 'Minnesota',
    slug: 'minnesota',
    status: 'gray-area',
    summary:
      'Minnesota has a large tribal gaming industry and has discussed sports betting for years without passing legislation. Online poker is not regulated, and the complex tribal-state relationship has made gambling expansion politically difficult.',
    keyFacts: [
      'Tribal gaming compacts allow full casino gaming on tribal lands',
      'Sports betting legislation has repeatedly failed due to tribal opposition',
      'No commercial casinos or licensed online poker exist',
      'Canterbury Park and Running Aces are licensed card clubs',
      'Offshore poker sites are the primary option for online play',
    ],
    cities: [
      { name: 'Minneapolis', slug: 'minneapolis' },
      { name: 'Saint Paul', slug: 'saint-paul' },
      { name: 'Rochester', slug: 'rochester' },
      { name: 'Duluth', slug: 'duluth' },
      { name: 'Bloomington', slug: 'bloomington' },
    ],
  },
  {
    name: 'Mississippi',
    slug: 'mississippi',
    status: 'gray-area',
    summary:
      'Mississippi has a prominent casino industry along the Gulf Coast and in Tunica, making it a significant land-based poker destination. Sports betting launched in 2018 but only at retail locations, and online poker remains unregulated.',
    keyFacts: [
      'Retail sports betting launched in August 2018 — one of the earliest states',
      'Major casinos along the Gulf Coast and in Tunica offer live poker',
      'No mobile sports betting or online poker is licensed in the state',
      'Mississippi has not pursued iGaming legislation',
      'Offshore sites serve online poker players without state oversight',
    ],
    cities: [
      { name: 'Jackson', slug: 'jackson' },
      { name: 'Gulfport', slug: 'gulfport' },
      { name: 'Southaven', slug: 'southaven' },
      { name: 'Hattiesburg', slug: 'hattiesburg' },
      { name: 'Biloxi', slug: 'biloxi' },
    ],
  },
  {
    name: 'Missouri',
    slug: 'missouri',
    status: 'gray-area',
    summary:
      "Missouri has a riverboat casino industry but has been slow to expand gambling. Sports betting legislation has repeatedly failed, and online poker is not regulated. The state's gambling landscape remains largely anchored to its licensed riverboats.",
    keyFacts: [
      'Thirteen licensed riverboat casinos operate across Missouri',
      'Sports betting legalization efforts have repeatedly failed',
      'No licensed online poker exists in Missouri',
      'Offshore poker sites serve residents without state oversight',
      'Conservative political environment has slowed gambling expansion',
    ],
    cities: [
      { name: 'Kansas City', slug: 'kansas-city' },
      { name: 'St. Louis', slug: 'st-louis' },
      { name: 'Springfield', slug: 'springfield' },
      { name: 'Columbia', slug: 'columbia' },
      { name: 'Independence', slug: 'independence' },
    ],
  },
  {
    name: 'Montana',
    slug: 'montana',
    status: 'gray-area',
    summary:
      'Montana has a unique gambling culture with video poker terminals legal in bars and restaurants, but no commercial casinos or licensed online poker. The state has a state-run sports betting app (Scoreboard) but no private online poker regulation.',
    keyFacts: [
      'Video poker terminals in licensed establishments are uniquely legal in Montana',
      'The state-run Scoreboard app launched mobile sports betting in 2020',
      'No commercial casinos or card rooms exist',
      'No private online poker operators are licensed',
      "Montana's state monopoly model is unlikely to expand to poker anytime soon",
    ],
    cities: [
      { name: 'Billings', slug: 'billings' },
      { name: 'Missoula', slug: 'missoula' },
      { name: 'Great Falls', slug: 'great-falls' },
      { name: 'Bozeman', slug: 'bozeman' },
      { name: 'Butte', slug: 'butte' },
    ],
  },
  {
    name: 'Nebraska',
    slug: 'nebraska',
    status: 'gray-area',
    summary:
      'Nebraska voters approved casino gambling in 2020 and commercial casinos began opening in 2023, marking a major shift for the historically gambling-averse state. Online poker remains unregulated as the new land-based market is still establishing itself.',
    keyFacts: [
      'Voters approved commercial casinos at horse racing tracks in 2020',
      'Casino gaming launched at licensed racetracks starting in 2023',
      'Sports betting launched alongside casino gaming at licensed facilities',
      'No licensed online poker framework exists yet',
      'As a new gambling state, online poker regulation is likely years away',
    ],
    cities: [
      { name: 'Omaha', slug: 'omaha' },
      { name: 'Lincoln', slug: 'lincoln' },
      { name: 'Bellevue', slug: 'bellevue' },
      { name: 'Grand Island', slug: 'grand-island' },
      { name: 'Kearney', slug: 'kearney' },
    ],
  },
  {
    name: 'Nevada',
    slug: 'nevada',
    status: 'legal',
    summary:
      'Nevada was the first state to license online poker in the modern era, launching regulated real-money play in 2013. Home to the World Series of Poker in Las Vegas, Nevada has a rich poker history and shares an online player pool with Delaware and New Jersey.',
    keyFacts: [
      'Online poker launched in April 2013 — the first state in the modern era',
      'WSOP.com and BetMGM Poker are licensed operators',
      'Nevada shares a multi-state player pool with Delaware and New Jersey',
      'Players must be physically located in Nevada to play for real money',
      "Las Vegas is home to the World Series of Poker and some of the world's best live poker rooms",
    ],
    cities: [
      { name: 'Las Vegas', slug: 'las-vegas' },
      { name: 'Henderson', slug: 'henderson' },
      { name: 'Reno', slug: 'reno' },
      { name: 'North Las Vegas', slug: 'north-las-vegas' },
      { name: 'Sparks', slug: 'sparks' },
    ],
  },
  {
    name: 'New Hampshire',
    slug: 'new-hampshire',
    status: 'gray-area',
    summary:
      "New Hampshire launched mobile sports betting in 2019 through a DraftKings monopoly and has a state lottery, but has not regulated online poker. The small state's gambling market is primarily centered on its lottery and licensed sports betting.",
    keyFacts: [
      'Sports betting launched in December 2019 through a DraftKings exclusivity arrangement',
      'The New Hampshire Lottery is a significant revenue source for the state',
      'No commercial casinos exist in New Hampshire',
      'No licensed online poker operators serve the state',
      'Offshore sites remain the primary online poker option for residents',
    ],
    cities: [
      { name: 'Manchester', slug: 'manchester' },
      { name: 'Nashua', slug: 'nashua' },
      { name: 'Concord', slug: 'concord' },
      { name: 'Derry', slug: 'derry' },
      { name: 'Dover', slug: 'dover' },
    ],
  },
  {
    name: 'New Jersey',
    slug: 'new-jersey',
    status: 'legal',
    summary:
      'New Jersey is the standard-bearer for regulated online poker in the United States. Launched in 2013, the NJ market features multiple competing operators, a shared player pool with Delaware and Nevada, and has been a model for other states considering regulation.',
    keyFacts: [
      'Online poker launched in November 2013 alongside online casino games',
      'Licensed operators include PokerStars NJ, WSOP NJ, BetMGM Poker, and Borgata Poker',
      'New Jersey shares a multi-state player pool with Delaware and Nevada',
      'The Atlantic City casino licenses anchor the online gaming framework',
      'New Jersey has generated hundreds of millions in online gaming tax revenue',
    ],
    cities: [
      { name: 'Newark', slug: 'newark' },
      { name: 'Jersey City', slug: 'jersey-city' },
      { name: 'Paterson', slug: 'paterson' },
      { name: 'Elizabeth', slug: 'elizabeth' },
      { name: 'Edison', slug: 'edison' },
    ],
  },
  {
    name: 'New Mexico',
    slug: 'new-mexico',
    status: 'gray-area',
    summary:
      'New Mexico has extensive tribal gaming operations under IGRA but no licensed online poker. Sports betting launched at tribal casinos in 2018 without specific legislation, but no online framework exists for poker.',
    keyFacts: [
      'Tribal casinos launched sports betting in October 2018 under existing compacts',
      'Numerous tribal gaming operations exist under federal compacts',
      'No state-licensed online poker exists',
      'New Mexico does not have a commercial casino industry',
      'Offshore poker sites serve residents without state-level oversight',
    ],
    cities: [
      { name: 'Albuquerque', slug: 'albuquerque' },
      { name: 'Las Cruces', slug: 'las-cruces' },
      { name: 'Rio Rancho', slug: 'rio-rancho' },
      { name: 'Santa Fe', slug: 'santa-fe' },
      { name: 'Roswell', slug: 'roswell' },
    ],
  },
  {
    name: 'New York',
    slug: 'new-york',
    status: 'gray-area',
    summary:
      'New York launched mobile sports betting in 2022 and quickly became the largest sports betting market in the US by handle. Despite this massive market, online poker regulation has not advanced, and residents rely on offshore platforms for online play.',
    keyFacts: [
      'Mobile sports betting launched in January 2022 with the highest tax rate in the nation (51%)',
      'New York is the largest sports betting market in the US by total handle',
      'Four commercial casinos operate in the state with more planned',
      'No licensed online poker platforms exist in New York',
      'Online poker legislation has been introduced but has not advanced in Albany',
    ],
    cities: [
      { name: 'New York City', slug: 'new-york-city' },
      { name: 'Buffalo', slug: 'buffalo' },
      { name: 'Rochester', slug: 'rochester' },
      { name: 'Yonkers', slug: 'yonkers' },
      { name: 'Syracuse', slug: 'syracuse' },
    ],
  },
  {
    name: 'North Carolina',
    slug: 'north-carolina',
    status: 'gray-area',
    summary:
      'North Carolina launched mobile sports betting in March 2024 and approved a limited number of commercial casinos, marking a significant shift in gambling policy. Online poker has not been part of any licensing discussions.',
    keyFacts: [
      'Mobile sports betting launched in March 2024 with multiple licensed operators',
      'Tribal gaming through the Eastern Band of Cherokee Indians operates two casinos',
      'Commercial casino legislation passed in 2023',
      'No licensed online poker sites serve North Carolina',
      'The large population makes NC a compelling potential online poker market',
    ],
    cities: [
      { name: 'Charlotte', slug: 'charlotte' },
      { name: 'Raleigh', slug: 'raleigh' },
      { name: 'Greensboro', slug: 'greensboro' },
      { name: 'Durham', slug: 'durham' },
      { name: 'Winston-Salem', slug: 'winston-salem' },
    ],
  },
  {
    name: 'North Dakota',
    slug: 'north-dakota',
    status: 'gray-area',
    summary:
      "North Dakota permits tribal gaming and some charitable gambling but has not expanded to commercial casinos or online poker. The state's conservative gambling environment has not generated significant momentum for iGaming.",
    keyFacts: [
      'Tribal gaming compacts allow casino gambling on tribal lands',
      'Charitable gambling including pull-tabs and bingo is permitted',
      'No commercial casinos or card rooms operate in North Dakota',
      'No licensed online poker platforms serve the state',
      'Sports betting has not yet been legalized',
    ],
    cities: [
      { name: 'Fargo', slug: 'fargo' },
      { name: 'Bismarck', slug: 'bismarck' },
      { name: 'Grand Forks', slug: 'grand-forks' },
      { name: 'Minot', slug: 'minot' },
      { name: 'West Fargo', slug: 'west-fargo' },
    ],
  },
  {
    name: 'Ohio',
    slug: 'ohio',
    status: 'gray-area',
    summary:
      'Ohio launched mobile sports betting in January 2023 and has four commercial casinos, making it a significant Midwest gambling market. Despite this, online poker and iGaming have not been part of the state\'s regulatory framework.',
    keyFacts: [
      'Mobile sports betting launched in January 2023 with a large pool of licensed operators',
      'Four commercial casinos operate: Hollywood Casino, JACK Cleveland, JACK Thistledown, and Hard Rock Cincinnati',
      'No licensed online poker sites serve Ohio residents',
      'iGaming legislation has been introduced but has not advanced',
      "Ohio's large population makes it a high-priority target for future online poker regulation",
    ],
    cities: [
      { name: 'Columbus', slug: 'columbus' },
      { name: 'Cleveland', slug: 'cleveland' },
      { name: 'Cincinnati', slug: 'cincinnati' },
      { name: 'Toledo', slug: 'toledo' },
      { name: 'Akron', slug: 'akron' },
    ],
  },
  {
    name: 'Oklahoma',
    slug: 'oklahoma',
    status: 'gray-area',
    summary:
      'Oklahoma has one of the largest tribal gaming industries in the country, with over 130 tribal gaming facilities. Sports betting is available through tribal compacts, but online poker regulation does not exist and the market is governed by tribal agreements.',
    keyFacts: [
      'Oklahoma has one of the most extensive tribal gaming markets in the US',
      'Sports betting launched through tribal gaming compacts in 2021',
      'No state-licensed online poker platforms exist',
      'Tribal sovereignty significantly shapes the gambling policy landscape',
      'Offshore sites serve residents for online poker',
    ],
    cities: [
      { name: 'Oklahoma City', slug: 'oklahoma-city' },
      { name: 'Tulsa', slug: 'tulsa' },
      { name: 'Norman', slug: 'norman' },
      { name: 'Broken Arrow', slug: 'broken-arrow' },
      { name: 'Edmond', slug: 'edmond' },
    ],
  },
  {
    name: 'Oregon',
    slug: 'oregon',
    status: 'gray-area',
    summary:
      'Oregon operates gambling through a state lottery monopoly that includes video poker machines in lottery retailers. The state launched a sports betting app (Scoreboard) through the lottery in 2019 but does not license private online poker operators.',
    keyFacts: [
      'The Oregon Lottery operates sports betting through its Scoreboard app since 2019',
      'Video lottery terminals including video poker are common in bars and lottery retailers',
      'No private online poker operators are licensed in Oregon',
      'The state lottery monopoly model limits expansion to private iGaming',
      'Tribal casinos operate on tribal lands under IGRA compacts',
    ],
    cities: [
      { name: 'Portland', slug: 'portland' },
      { name: 'Salem', slug: 'salem' },
      { name: 'Eugene', slug: 'eugene' },
      { name: 'Gresham', slug: 'gresham' },
      { name: 'Hillsboro', slug: 'hillsboro' },
    ],
  },
  {
    name: 'Pennsylvania',
    slug: 'pennsylvania',
    status: 'legal',
    summary:
      'Pennsylvania launched regulated online poker in November 2019 as part of a sweeping gaming expansion law. It is one of the largest iGaming markets in the US, with multiple operators competing for players and strong tax revenue for the state.',
    keyFacts: [
      'Online poker launched in November 2019 under the 2017 Gaming Expansion Act',
      'Licensed operators include PokerStars PA, WSOP PA, BetMGM Poker, and Borgata Poker',
      'Pennsylvania has a 16% gross revenue tax on online poker — among the lowest rates',
      'Players must be physically located in Pennsylvania to play for real money',
      'Pennsylvania participates in multi-state player pool agreements with other regulated states',
    ],
    cities: [
      { name: 'Philadelphia', slug: 'philadelphia' },
      { name: 'Pittsburgh', slug: 'pittsburgh' },
      { name: 'Allentown', slug: 'allentown' },
      { name: 'Erie', slug: 'erie' },
      { name: 'Reading', slug: 'reading' },
    ],
  },
  {
    name: 'Rhode Island',
    slug: 'rhode-island',
    status: 'gray-area',
    summary:
      'Rhode Island operates gambling through a state lottery monopoly and launched sports betting through the lottery in 2018. No private online poker operators are licensed, and the state has not pursued iGaming beyond its lottery-operated offerings.',
    keyFacts: [
      'Sports betting launched in November 2018 through Twin River casinos under the state lottery',
      'Two commercial casinos (Twin River Lincoln and Tiverton) operate under state oversight',
      'No private online poker operators are licensed',
      'The lottery monopoly model makes private iGaming expansion unlikely in the near term',
      'Offshore sites serve residents without state-level protection',
    ],
    cities: [
      { name: 'Providence', slug: 'providence' },
      { name: 'Cranston', slug: 'cranston' },
      { name: 'Warwick', slug: 'warwick' },
      { name: 'Pawtucket', slug: 'pawtucket' },
      { name: 'East Providence', slug: 'east-providence' },
    ],
  },
  {
    name: 'South Carolina',
    slug: 'south-carolina',
    status: 'gray-area',
    summary:
      'South Carolina has some of the most restrictive gambling laws in the Southeast, with no commercial casinos, no lottery, and no licensed gaming of any kind beyond charitable bingo. Online poker is entirely unregulated and politically contentious.',
    keyFacts: [
      'South Carolina is one of the few states with no state lottery',
      'Commercial casinos and card rooms are prohibited',
      'Gambling is broadly prohibited under state law',
      'No licensed online poker exists',
      'Gambling expansion proposals rarely advance in the state legislature',
    ],
    cities: [
      { name: 'Columbia', slug: 'columbia' },
      { name: 'Charleston', slug: 'charleston' },
      { name: 'North Charleston', slug: 'north-charleston' },
      { name: 'Mount Pleasant', slug: 'mount-pleasant' },
      { name: 'Rock Hill', slug: 'rock-hill' },
    ],
  },
  {
    name: 'South Dakota',
    slug: 'south-dakota',
    status: 'gray-area',
    summary:
      'South Dakota is home to Deadwood, a historic gambling town, and tribal casinos. Sports betting launched in 2021 after a voter referendum. Online poker is not regulated, though the state has one of the more gambling-friendly cultures in the Great Plains.',
    keyFacts: [
      'Sports betting launched in September 2021 following a 2020 voter approval',
      'Deadwood hosts numerous licensed casinos with a $100 maximum bet',
      'Tribal gaming operates on multiple reservations across the state',
      'No licensed online poker framework exists',
      'Offshore sites serve residents for online poker play',
    ],
    cities: [
      { name: 'Sioux Falls', slug: 'sioux-falls' },
      { name: 'Rapid City', slug: 'rapid-city' },
      { name: 'Aberdeen', slug: 'aberdeen' },
      { name: 'Brookings', slug: 'brookings' },
      { name: 'Watertown', slug: 'watertown' },
    ],
  },
  {
    name: 'Tennessee',
    slug: 'tennessee',
    status: 'gray-area',
    summary:
      'Tennessee was the first state to launch online-only sports betting without any land-based casino requirement, going live in November 2020. Despite this progressive sports betting approach, online poker and casino gaming remain unregulated in the state.',
    keyFacts: [
      'Online-only sports betting launched in November 2020 — a first-of-its-kind model',
      'No commercial casinos or card rooms exist in Tennessee',
      'No licensed online poker operators serve the state',
      "Tennessee's sports betting tax rate is 20% on gross revenue",
      "Online poker legislation has not been part of the state's iGaming conversation",
    ],
    cities: [
      { name: 'Nashville', slug: 'nashville' },
      { name: 'Memphis', slug: 'memphis' },
      { name: 'Knoxville', slug: 'knoxville' },
      { name: 'Chattanooga', slug: 'chattanooga' },
      { name: 'Clarksville', slug: 'clarksville' },
    ],
  },
  {
    name: 'Texas',
    slug: 'texas',
    status: 'gray-area',
    summary:
      "Texas has very strict gambling laws, with commercial gambling essentially prohibited outside of tribal lands and horse racing. Despite a large, active poker community and home to the Texas Hold'em variant, online poker is not regulated and the political environment has been hostile to expansion.",
    keyFacts: [
      'Texas has two federally recognized tribal casinos (Speaking Rock and Naskila)',
      'Charity poker events operate in a legal gray area under social gambling provisions',
      'No commercial casinos, sports betting, or online poker are licensed',
      "The Texas Hold'em poker variant originated in the state (Robstown, TX)",
      'Gambling expansion faces significant opposition in the Texas legislature',
    ],
    cities: [
      { name: 'Houston', slug: 'houston' },
      { name: 'San Antonio', slug: 'san-antonio' },
      { name: 'Dallas', slug: 'dallas' },
      { name: 'Austin', slug: 'austin' },
      { name: 'Fort Worth', slug: 'fort-worth' },
    ],
  },
  {
    name: 'Utah',
    slug: 'utah',
    status: 'illegal',
    summary:
      "Utah has a constitutionally enshrined prohibition on gambling, making it one of only two states (with Hawaii) where essentially all gambling is illegal. There is no lottery, no casinos, no licensed card rooms, and no online poker of any kind. The prohibition reflects the state's predominant religious culture.",
    keyFacts: [
      'The Utah Constitution prohibits gambling — an extremely rare state-level ban',
      'No lottery, no casinos, no card rooms, and no licensed gambling exist',
      'Utah is one of only two states with a total constitutional gambling prohibition',
      'There is broad cross-party political support for maintaining the ban',
      'No online poker legislation has ever been seriously considered',
    ],
    cities: [
      { name: 'Salt Lake City', slug: 'salt-lake-city' },
      { name: 'West Valley City', slug: 'west-valley-city' },
      { name: 'Provo', slug: 'provo' },
      { name: 'West Jordan', slug: 'west-jordan' },
      { name: 'Orem', slug: 'orem' },
    ],
  },
  {
    name: 'Vermont',
    slug: 'vermont',
    status: 'gray-area',
    summary:
      "Vermont legalized sports betting in 2023 and launched a mobile market in early 2024, but has not moved to regulate online poker. The small state's gambling market is limited, and iGaming has not been part of legislative discussions.",
    keyFacts: [
      'Sports betting launched in January 2024 through a DraftKings exclusivity arrangement',
      'No commercial casinos operate in Vermont',
      'Vermont has a state lottery but no casino-style games',
      'No licensed online poker exists',
      'The small population limits the economic case for standalone online poker regulation',
    ],
    cities: [
      { name: 'Burlington', slug: 'burlington' },
      { name: 'South Burlington', slug: 'south-burlington' },
      { name: 'Rutland', slug: 'rutland' },
      { name: 'Barre', slug: 'barre' },
      { name: 'Montpelier', slug: 'montpelier' },
    ],
  },
  {
    name: 'Virginia',
    slug: 'virginia',
    status: 'gray-area',
    summary:
      'Virginia legalized sports betting in 2020 and has rapidly become one of the larger sports betting markets on the East Coast. Commercial casinos are under development in several cities, but online poker has not been part of the gaming expansion legislation.',
    keyFacts: [
      'Mobile sports betting launched in January 2021 with multiple licensed operators',
      'Commercial casino legislation was approved in 2020 with several in development',
      'No licensed online poker platforms serve Virginia',
      'iGaming legislation has been discussed but not passed',
      "Virginia's growing population and tech-forward economy make it a future iGaming candidate",
    ],
    cities: [
      { name: 'Virginia Beach', slug: 'virginia-beach' },
      { name: 'Norfolk', slug: 'norfolk' },
      { name: 'Chesapeake', slug: 'chesapeake' },
      { name: 'Richmond', slug: 'richmond' },
      { name: 'Newport News', slug: 'newport-news' },
    ],
  },
  {
    name: 'Washington',
    slug: 'washington',
    status: 'illegal',
    summary:
      'Washington State explicitly criminalized online gambling under RCW 9.46.240, making playing on unauthorized online poker sites a Class C felony. Despite this, the law has rarely been enforced against players. Washington has extensive tribal gaming but no licensed online poker.',
    keyFacts: [
      'Online gambling is a Class C felony under Washington State law (RCW 9.46.240)',
      'The law applies to players, not just operators',
      'Enforcement against individual players has been virtually nonexistent',
      'Tribal gaming is extensive, with dozens of casinos operating statewide',
      'Sports betting launched through tribal casinos in 2021 under new legislation',
    ],
    cities: [
      { name: 'Seattle', slug: 'seattle' },
      { name: 'Spokane', slug: 'spokane' },
      { name: 'Tacoma', slug: 'tacoma' },
      { name: 'Vancouver', slug: 'vancouver' },
      { name: 'Bellevue', slug: 'bellevue' },
    ],
  },
  {
    name: 'West Virginia',
    slug: 'west-virginia',
    status: 'legal',
    summary:
      'West Virginia passed iGaming legislation in 2019 and launched online poker in 2020, becoming one of the smallest states to offer regulated online poker. WSOP is the primary licensed operator, and the state participates in multi-state player pool sharing.',
    keyFacts: [
      'Online poker launched in 2020 under the West Virginia Lottery Interactive Wagering Act',
      'WSOP is the primary licensed poker operator in the state',
      'West Virginia participates in multi-state player pool agreements',
      'Players must be physically located in West Virginia to play for real money',
      'The small population means multi-state liquidity is critical for game availability',
    ],
    cities: [
      { name: 'Charleston', slug: 'charleston' },
      { name: 'Huntington', slug: 'huntington' },
      { name: 'Morgantown', slug: 'morgantown' },
      { name: 'Parkersburg', slug: 'parkersburg' },
      { name: 'Wheeling', slug: 'wheeling' },
    ],
  },
  {
    name: 'Wisconsin',
    slug: 'wisconsin',
    status: 'gray-area',
    summary:
      'Wisconsin has a significant tribal gaming industry but no commercial casinos or licensed online poker. The state has been resistant to gambling expansion beyond tribal compacts, and sports betting legislation has not passed.',
    keyFacts: [
      'Tribal gaming compacts cover full casino gaming on tribal lands',
      'No commercial casinos or card rooms operate outside tribal lands',
      'Sports betting legislation has not passed in Wisconsin',
      'No licensed online poker platforms serve Wisconsin residents',
      'Offshore sites are the primary option for online poker players',
    ],
    cities: [
      { name: 'Milwaukee', slug: 'milwaukee' },
      { name: 'Madison', slug: 'madison' },
      { name: 'Green Bay', slug: 'green-bay' },
      { name: 'Kenosha', slug: 'kenosha' },
      { name: 'Racine', slug: 'racine' },
    ],
  },
  {
    name: 'Wyoming',
    slug: 'wyoming',
    status: 'gray-area',
    summary:
      'Wyoming was one of the first states to legalize mobile sports betting without a land-based casino requirement, launching in September 2021. No commercial casinos exist in the state, and online poker is unregulated, though the sports betting precedent shows openness to online gambling.',
    keyFacts: [
      'Mobile sports betting launched in September 2021 — online-only model',
      'No commercial casinos exist in Wyoming',
      'Tribal gaming operates at one facility under a federal compact',
      'No licensed online poker platforms serve the state',
      "Wyoming's online-first sports betting model is encouraging for future iGaming",
    ],
    cities: [
      { name: 'Cheyenne', slug: 'cheyenne' },
      { name: 'Casper', slug: 'casper' },
      { name: 'Laramie', slug: 'laramie' },
      { name: 'Gillette', slug: 'gillette' },
      { name: 'Rock Springs', slug: 'rock-springs' },
    ],
  },
]
